import React, { useState } from 'react';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LanguageIcon from '@mui/icons-material/Language';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LiveTvIcon from '@mui/icons-material/LiveTv';

const Metrics = ({ metrics = [], content = {} }) => {
  const theme = useTheme();

  const [viewPortEntered, setViewPortEntered] = useState(false);
  const setViewPortVisibility = (isVisible) => {
    if (viewPortEntered) {
      return;
    }

    setViewPortEntered(isVisible);
  };

  // Función para obtener el icono basado en el nombre
  const getIcon = (iconName) => {
    const iconProps = { sx: { fontSize: { xs: 22, md: 26 }, color: theme.palette.primary.main } };
    
    switch (iconName) {
      case 'LanguageIcon':
        return <LanguageIcon {...iconProps} />;
      case 'YouTubeIcon':
        return <YouTubeIcon {...iconProps} />;
      case 'TwitterIcon':
        return <TwitterIcon {...iconProps} />;
      case 'InstagramIcon':
        return <InstagramIcon {...iconProps} />;
      case 'OndemandVideoIcon':
        return <OndemandVideoIcon {...iconProps} />;
      case 'LiveTvIcon':
        return <LiveTvIcon {...iconProps} />;
      default:
        return <LanguageIcon {...iconProps} />;
    }
  };

  // Usar métricas de la API o datos por defecto
  const metricsData = metrics.length > 0 ? metrics.map(metric => ({
    ...metric,
    icon: getIcon(metric.icon),
  })) : [
    {
      title: 30000,
      label: 'Visitas mensuales que pueden descubrir tu contenido en la web',
      subtitle: 'Alcance constante y crecimiento sostenido.',
      suffix: '+',
      icon: getIcon('LanguageIcon'),
    },
    {
      title: 90000,
      label: 'Seguidores en redes sociales para amplificar tus clips',
      subtitle: 'Instagram, X y redes combinados.',
      suffix: '+',
      icon: getIcon('InstagramIcon'),
    },
    {
      title: 40,
      label: 'Producciones originales que ya confían en nuestra red',
      subtitle: 'Desarrolladas en el Media Lab.',
      suffix: '+',
      icon: getIcon('OndemandVideoIcon'),
    },
    {
      title: 25,
      label: 'Programas en vivo que generan tracción diaria',
      subtitle: 'Con alcance global.',
      suffix: '+',
      icon: getIcon('LiveTvIcon'),
    },
  ];

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{
            textTransform: 'uppercase',
            fontWeight: 'medium',
          }}
          gutterBottom
          color={'secondary'}
          align={'center'}
        >
          TNO Media Lab
        </Typography>
        <Box
          component={Typography}
          fontWeight={700}
          variant={'h3'}
          align={'center'}
          gutterBottom
        >
          Nuestros números
        </Box>
        <Typography
          variant={'h6'}
          align={'center'}
          color="textSecondary"
          sx={{ mb: 4 }}
        >
          El Lab produce tu show, lo estrena en el streaming en vivo de TNO y lo publica en on-demand, clips, tnonetwork.com y en todas las plataformas donde quieras estar.
        </Typography>
      </Box>
      <Box marginBottom={{ xs: 4, sm: 6, md: 6 }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {metricsData.map((item, i) => (
            <Grid key={i} item xs={6} sm={3}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom={0.75}
              >
                {item.icon}
              </Box>
              <Typography
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.35rem', sm: '1.6rem', md: '1.85rem' },
                  lineHeight: 1.15,
                }}
              >
                <VisibilitySensor
                  onChange={(isVisible) => setViewPortVisibility(isVisible)}
                  delayedCall
                >
                  <CountUp
                    redraw={false}
                    end={viewPortEntered ? item.title : 0}
                    start={0}
                    suffix={item.suffix}
                    separator="."
                  />
                </VisibilitySensor>
              </Typography>
              <Typography
                align="center"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                  fontWeight: 600,
                  lineHeight: 1.3,
                  px: 0.5,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                color="text.secondary"
                align="center"
                component="p"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  lineHeight: 1.35,
                  mt: 0.5,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {item.subtitle}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Metrics;
